/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppButton, getAppButtonStyles, getSecondaryButtonOverrideStyles } from '../../components';
import { Label, Dialog, DialogFooter, Spinner, SpinnerType } from 'office-ui-fabric-react';
import type { IDialogProps, IDialogFooterProps } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { getSpinnerStyles, getRecoveringPageStylesIfRecoveryFlowTranscriptionEnabled } from '../../common/pageStyles/RecoveringPage.styles';
import { TranscriptionRoutePathNames } from '../../routes';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { CancelRecoveryTask, GetAudioBackupRecoveryTaskResultStatus, getAudioFile, RecoverFromBackupInTranscriptionRecoveryFlow, SetAudioFile, SetRecoveredTranscriptionFileInTranscriptionRecoveryFlow, SetRecoveryFlowCancelDialog } from '../../common/actions/RecoveringActions';
import { SetFailureStateAndRouteToFailure } from '../../actions/FailureActions';
import { RecoveryKeys } from '../../common/telemetry/RecoveryKeys';
import { ErrorStringFromException, isEmptyString } from '../../../utils/utils';
import { AppAuthenticationProviderContext } from '../../context';
import { ITranscriptionSourceFile } from '../../../backend/interfaces/ITranscriptionSourceFileProvider';
import { HostAppSettings, HostAppSettingName } from '../../../backend/office-js/HostAppSettings';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import recoveringStore from '../../stores/RecoveringStore';
import { ChangeEvent } from '../../stores/utils';
import { AuthPopup } from '../../components/Popups/AuthPopup';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { TranscriptionTelemetryErrorCodes } from '../../../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../../../backend/interfaces/ITranscriptionErrorEvent';
import { getDialogContentProps, getDialogStyles, getModalProps, getTopLabelStyles } from '../../common/styles/Popup.styles';
import { TaskListResultStatus } from '../../../backend/transcriptionManager/TaskList';
import { FailureContinuation } from '../../common/enums/failureEnums';
import { IndexedDBErrorKeys } from '../../common/errors/IndexedDBErrorKeys';
import { RecoveryFlowErrorKeys } from '../../common/errors/RecoveryFlowErrorKeys';
import { NavigateToLandingPageAfterAudioBackupCheck } from '../../actions/LandingActions';
import { TranscriptionAlert } from '../../components/Popups/TranscriptionAlert';
import { SetForegroundTaskInStore } from '../../common/actions/LoadingActions';
import { FileUploadForegroundTask } from '../../../backend/fileUpload/FileUploadForegroundTask';
import { TranscriptionIllustration } from '../../common/images/TranscriptionIllustration';
import { EMPTY_STRING, FileUploadEntryPoints, WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS } from '../../../AppConstants';
import { GetNetworkConnectionMonitorInstance, InternetState, NetworkConnectionListener } from '../../NetworkConnectionMonitor';
import { v4 as uuidv4 } from 'uuid';
import { LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from '../../LoadTranscriptionStrings';
import { LogError } from '../../../utils/ErrorTelemetry';
import { GetPrimaryIdentity } from '../../../backend/office-js/auth/IdentityUtil';
import { IAuthenticationProvider, PrimaryIdentityInfo } from '../../../backend/interfaces/IAuthenticationProvider';
import { getPrimaryAccountInfoString, shouldShowOnedriveInfobar } from '../../pages/Utils';

const DialogWithChildren = Dialog as React.FunctionComponent<React.PropsWithChildren<IDialogProps>>;
const DialogFooterWithChildren = DialogFooter as React.FunctionComponent<React.PropsWithChildren<IDialogFooterProps>>

export interface RecoveringState {
  recoveredTranscriptionFile: ITranscriptionSourceFile | null;
  isAudioFileFromDBRecovered: boolean;
  shouldShowRecoveryCancelDialog: boolean;
  file: File | null;
}

export class Recovering extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>, RecoveringState> implements NetworkConnectionListener{
  declare context: React.ContextType<typeof AppAuthenticationProviderContext>;
  static contextType = AppAuthenticationProviderContext;
  private _isMounted: boolean;
  private primaryIdentity : PrimaryIdentityInfo | null =null;
  private waitAndPingRequestId: string = EMPTY_STRING;

  constructor(props: RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>) {
    super(props);
    this._isMounted = false;

    this.state = recoveringStore.getState();
    recoveringStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async componentWillMount() {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.ShouldShowOneDriveIdentityInfoDialog)) {
      this.updatePrimaryIdentity();
    }
    await this._performAudioFileRecoveryTasksFromDB()
  }

  public componentWillUnmount() {
    this._isMounted = false;
    GetNetworkConnectionMonitorInstance().removeListener(this);
    if (!isEmptyString(this.waitAndPingRequestId)) {
      GetNetworkConnectionMonitorInstance().clearWaitAndPingRequest(this.waitAndPingRequestId);
      this.waitAndPingRequestId = EMPTY_STRING;
    }
  }

  public async componentDidMount() {
    this._isMounted = true;
    GetNetworkConnectionMonitorInstance().addListener(this);
  }

  onInternetConnectionStatusChanged(state: InternetState): void {
    switch(state) {
      case InternetState.INTERNET_DISCONNECTED: {
        if (!this.state.recoveredTranscriptionFile) {

          LogError(LogEventNames.Warning, "Network Error during Recovery. " + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);

          /* this check is to avoid internet updates while waitAndPingAfter defined timer is running, this
          is to avoid ping/pong between connected/disconnected state and prevent jumping to failure screen due to this,
          failure screen will be Shown , after waiting for network Recovery.*/
          if (!isEmptyString(this.waitAndPingRequestId)) {
            return;
          }

          this.waitAndPingRequestId = uuidv4();
          GetNetworkConnectionMonitorInstance().waitAndPingAfter(WAIT_TIME_FOR_NETWORK_RECONNECT_MILLIS, this.waitAndPingRequestId).
            then((pingConnected) => {
              this.waitAndPingRequestId = EMPTY_STRING;
              if (!this._isMounted) {
                return;
              }

              // If network is reconnected/OK, then no need to post process offline
              // if network is still NOT reconnected, then post process offline.
              if (!pingConnected) {
                let transcriptionErrorEvent: ITranscriptionErrorEvent | undefined = { errorEventMessage: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE };

                // TranscriptionStrings can be null if the user goes offline before TranscriptionStrings is loaded during boot.
                if (TranscriptionStrings != null) {
                  transcriptionErrorEvent = { errorEventMessage: TranscriptionStrings.RecordingLostConnectionFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionFailureReason };
                }

                // Navigate to the failure page.
                SetFailureStateAndRouteToFailure(
                  LogEventNames.Warning,
                  transcriptionErrorEvent!,
                  FailureContinuation.RecoveryonInternetConnected,
                  this.props.history);
              }
            });
        }
      }
      break;
    }
  }

  public render() {
    const recoveryPageUI = this.state.isAudioFileFromDBRecovered ? this._getRecoveryCompletedUIInTranscriptionRecoveryFlow() : this._getRecoveryProgressUIInTranscriptionRecoveryFlow()

    return (
      <div>
        {recoveryPageUI}
        <AuthPopup />
        <TranscriptionAlert/>
        {!this.state.recoveredTranscriptionFile && this.state.shouldShowRecoveryCancelDialog ? this.showRecoveryFlowDialog() : null}
      </div>
    );
  }

  private getFailureReasonForRecovering(errorString: string) : ITranscriptionErrorEvent {
    let transcriptionErrorEvent: ITranscriptionErrorEvent;

    // TODO: do parsing and recheck if there is any common field for it
    // Index DB errors and recovery flow errors with message File upload failed, please try again later.
    if (
      errorString.indexOf(IndexedDBErrorKeys.AbortError) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.ConstraintError) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.QuotaExceededError) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.VersionError) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.UnknownError) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.ItemNotFound) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.SourceFileNotFound) != -1 ||
      errorString.indexOf(IndexedDBErrorKeys.FragmentOverlap) != -1 ||
      errorString.indexOf(RecoveryFlowErrorKeys.InvalidRangeError) != -1 ||
      errorString.indexOf(RecoveryFlowErrorKeys.InvalidRequestError) != -1 ||
      errorString.indexOf(RecoveryFlowErrorKeys.RangeError) != -1
    ) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.FileRecoveryFailedFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonUnresolvableSystemErrors};
    } else if (errorString.indexOf(RecoveryFlowErrorKeys.ProvisioningNotAllowedError) != -1) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.InvalidLicense, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonInvalidLicense};
    } else if (errorString.indexOf(RecoveryFlowErrorKeys.UnableToFetchTokenError) != -1 || errorString.indexOf(RecoveryFlowErrorKeys.InvalidAuthenticationToken) != -1 || errorString.indexOf(RecoveryFlowErrorKeys.ConditionalAccessPolicyEnforced) != -1) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.AuthenticationError, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonFetchTokenFailed};
    } else if (errorString.indexOf(RecoveryFlowErrorKeys.OneDriveQuotaLimitReachedError) != -1 || errorString.indexOf(RecoveryFlowErrorKeys.QuotaLimitReachedError) != -1) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.QuotaLimitExceeded, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonQuotaLimitExceed};
    } else if (errorString.indexOf(RecoveryFlowErrorKeys.ExpiredTokenError) != -1) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.SessionExpired, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonSessionExpired};
    } else if (errorString.indexOf(RecoveryFlowErrorKeys.ResourceLockedError) != -1) {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.PermissionsError, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonResourceLocked};
    } else {
      transcriptionErrorEvent = {errorEventMessage: TranscriptionStrings.FileRecoveryFailedFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReasonUnknown};
    }

    return transcriptionErrorEvent;
  }

  private _getRecoveryCompletedUIInTranscriptionRecoveryFlow() {
    const styles = getRecoveringPageStylesIfRecoveryFlowTranscriptionEnabled();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div className={styles.labelContainer}>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.RecoveryPageLabel}</Label>
        </div>
        <div className={styles.buttonContainer}>
          <AppButton
            type={'AppButtonProps'}
            id="SaveAndTranscribeNowButton"
            styles={getAppButtonStyles()}
            text={TranscriptionStrings.TranscribeNowButtonLabel}
            onExecute={() => {
              this._transcribe();
            }}
          />
          <div className={styles.gotItButtonContainer}>
            <AppButton
              type={'AppButtonProps'}
              id="DiscardButton"
              styles={getSecondaryButtonOverrideStyles()}
              text={TranscriptionStrings.DiscardButtonLabel}
              onExecute={() => {
                this._onDiscard();
              }}
            />
          </div>
        </div>
        <div className={styles.imageContainer}>
          <TranscriptionIllustration />
        </div>
      </div>
    );
  }

  private _onDiscard = async () =>  {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery - User opted to discard the recovered file"}]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
    // explicitly initialisation is required before navigating to next page
    if (this.props.location.state.audioBackup != null) {
      await this.props.location.state.audioBackup.ClearBackup();
      await this.props.location.state.audioBackup.Initialize();
    }

    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
      }
      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    } else {
      if (this.props.location.state.audioBackup != null ) {
        NavigateToLandingPageAfterAudioBackupCheck({audioBackup: this.props.location.state.audioBackup, needsRecovery: false}, this.props.history );
      }
    }
    LogEvents.RenderToPane.EndDurationMeasure(RenderToPaneKeys.DurationSecs);
  }

  private _transcribe = async () => {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery - User opted to save and transcribe the recovered file"}]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
    if (this.state.file != null) {
      SetForegroundTaskInStore(new FileUploadForegroundTask(this.state.file, this.context as IAuthenticationProvider, this.props.history, FileUploadEntryPoints.RECOVERING, this.props.location.state.audioBackup));
    }

    this.props.history.replace(TranscriptionRoutePathNames.Loading);

    if (this.props.location.state.audioBackup != null) {
      this.props.location.state.audioBackup.Close();
    }

    this.props.location.state.realTimeTranscriptionConnection = null;
    LogEvents.RenderToPane.EndDurationMeasure(RenderToPaneKeys.DurationSecs);
}

  private _getRecoveryProgressUIInTranscriptionRecoveryFlow() {
    const styles = getRecoveringPageStylesIfRecoveryFlowTranscriptionEnabled();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div className={styles.labelContainer}>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.RecoveryPageLabel}</Label>
        </div>
        <div className={styles.buttonContainer}>
          <AppButton
            type={'AppButtonProps'}
            id="SaveAndTranscribeNowButton"
            styles={getAppButtonStyles()}
            text={TranscriptionStrings.TranscribeNowButtonLabel}
            disabled={true}
            onExecute={() => {
              this._transcribe();
            }}
          />
          <div className={styles.gotItButtonContainer}>
            <AppButton
              type={'AppButtonProps'}
              id="DiscardButton"
              styles={getSecondaryButtonOverrideStyles()}
              text={TranscriptionStrings.DiscardButtonLabel}
              onExecute={() => {
                this._onDiscard();
              }}
            />
          </div>
        </div>
        <div className={styles.spinnerContainer}>
          <Spinner type={SpinnerType.large} styles={getSpinnerStyles()} />
        </div>
        <div>
        { shouldShowOnedriveInfobar(this.primaryIdentity) ?
          <div style= { AppLayoutSettings.AppCommonStyles.getOneDriveIdentityInfoBarStyles(AppLayoutSettings.AppCommonStyles.getOneDriveIdentityInfoBarOnPageBottomStyles()) } >
            { getPrimaryAccountInfoString(this.primaryIdentity) }
          </div> : null }
      </div>
      </div>
    );

  }

  private showRecoveryFlowDialog() {
    const recoveryFlowDialogTitle: JSX.Element =
    <Label styles={getTopLabelStyles()}>
    {TranscriptionStrings.RecoveryFlowDialogTitle}
    </Label>

    return <DialogWithChildren
      dialogContentProps={getDialogContentProps(recoveryFlowDialogTitle)}
      modalProps={getModalProps()}
      hidden={!this.state.shouldShowRecoveryCancelDialog}
      styles={getDialogStyles()}>
      <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.RecoveryFlowDialogDescription}</Label>
      <DialogFooterWithChildren>
        <AppButton
          type={'AppButtonProps'}
          id="RecoveryFlowNoButtonID"
          styles={getSecondaryButtonOverrideStyles()}
          text={TranscriptionStrings.RecoveryFlowDialogNoButtonLabelText}
          onExecute={this._onNoClicked}
        />
        <AppButton
          type={'AppButtonProps'}
          id="RecoveryFlowYesButtonID"
          text={TranscriptionStrings.RecoveryFlowDialogYesButtonLabelText}
          onExecute={this._onYesClicked}
        />
      </DialogFooterWithChildren>
    </DialogWithChildren>
  }

  private _onNoClicked() {
    SetRecoveryFlowCancelDialog(false);
  }

  private _onYesClicked = async () => {
    // cancel ongoing recovery task
    await CancelRecoveryTask();
    if (this.props.location.state.audioBackup != null) {
      await this.props.location.state.audioBackup.ClearBackup();
      await this.props.location.state.audioBackup.Initialize();
    }

    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
      }
      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    } else {
      if (this.props.location.state.audioBackup != null ) {
        NavigateToLandingPageAfterAudioBackupCheck({audioBackup: this.props.location.state.audioBackup, needsRecovery: false}, this.props.history );
      }
    }
    SetRecoveryFlowCancelDialog(false);
    const reason: string = "operation was canceled by user";
    LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: reason}]);
  }

  private async _performAudioFileRecoveryTasksFromDB() {
    try {
      LogEvents.Recovery.Start();

      const recoveredFileInTranscriptionRecoveryFlow: File | null = await RecoverFromBackupInTranscriptionRecoveryFlow(this.props.location.state.audioBackup!, this.context!);

      // Update recovery page state, if file recovered successfully.
      if (recoveredFileInTranscriptionRecoveryFlow != null) {
        LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: true}]);
        let file = getAudioFile();
        if (file != null) {
          SetAudioFile(file);
          SetRecoveredTranscriptionFileInTranscriptionRecoveryFlow(true);
        } else {
          throw ('recovered file is null');
        }
      } else {
        const uploadResultStatus: TaskListResultStatus = GetAudioBackupRecoveryTaskResultStatus();
        // if operation is not cancelled, we will navigate to loading or landing depending on condition
        if (uploadResultStatus != TaskListResultStatus.Canceled) {
          const reason: string = "operation was canceled";
          LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: reason}]);
          this._navigateAway(reason);
        }
      }
    } catch (e) {
      let errorString = ErrorStringFromException(e);
      LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: errorString}]);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      const transcriptionErrorEvent: ITranscriptionErrorEvent = this.getFailureReasonForRecovering(errorString);

      SetFailureStateAndRouteToFailure(
        LogEventNames.Recovery,
        transcriptionErrorEvent,
        FailureContinuation.Cancel /*failureContinuation*/,
        this.props.history,
      );

    }
  }

  private _navigateAway(reason: string): void {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery - " + reason }]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    } else {
      if (this.props.location.state.audioBackup != null ) {
        NavigateToLandingPageAfterAudioBackupCheck({audioBackup: this.props.location.state.audioBackup, needsRecovery: false}, this.props.history );
      }
    }
  };

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(recoveringStore.getState());
    }
  }

  private async updatePrimaryIdentity() {
    this.primaryIdentity = await GetPrimaryIdentity();
    this.forceUpdate();
  }
};